import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth } from "../config/config-firebase";
import { onIdTokenChanged } from "firebase/auth";
import API_BASE from "../config/api";
import BottomTabBar from "../components/BottomTabBar";
import { ACCESS_DISABLED_MESSAGE } from "../hooks/useMerchantStores";
import { isPromoLoginMode } from "../utils/adminMerchantMode";
import WorkflowSection from "../components/workflow/WorkflowSection";

export default function MerchantHome() {
  const navigate = useNavigate();
  const location = useLocation();
  const [contactName, setContactName] = useState("");
  const [loading, setLoading] = useState(true);
  // { subtotal, tax, total, currency } from the just-completed Checkout
  // Session, in the smallest currency unit (e.g. cents) — same convention
  // Stripe itself uses. Only set right after a successful checkout
  // redirect; cleared once dismissed.
  const [paymentSummary, setPaymentSummary] = useState(null);

  // Stripe redirects here as .../home?checkout=success&session_id=cs_...
  // right after a successful Checkout. Confirm it immediately server-side
  // (webhook-independent — see onboardingController.confirmCheckoutSession)
  // instead of waiting on the webhook to arrive, then strip the query
  // params so a refresh doesn't re-confirm the same session.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("checkout") !== "success") return;

    const sessionId = params.get("session_id");
    if (!sessionId) return;

    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/onboarding/checkout/confirm?session_id=${encodeURIComponent(sessionId)}`
        );
        const data = await res.json();
        if (!res.ok || !data.success) {
          console.error("checkout/confirm failed:", data.message);
        } else {
          console.log(
            `Subscription confirmed: ${data.stripe_subscription_id} (${data.stripe_subscription_status})`
          );
          if (typeof data.amount_total === "number" && data.currency) {
            setPaymentSummary({
              subtotal: data.amount_subtotal,
              discount: data.amount_discount || 0,
              tax: data.amount_tax,
              total: data.amount_total,
              currency: data.currency,
            });
          }
        }
      } catch (err) {
        console.error("checkout/confirm request failed:", err);
      } finally {
        navigate("/home", { replace: true });
      }
    })();
  }, [location.search, navigate]);

  useEffect(() => {
    const customerId = localStorage.getItem("customer_id");
    if (!customerId) {
      navigate("/login");
      return;
    }

    // Every uid-based fetch re-checks is_teammember / is_teammember_active
    // (now exposed by merchantDetails/:uid) so a team member who gets
    // disabled mid-session is signed out the next time this screen loads,
    // not just at their next OTP login.
    const fetchDetails = async (id, useCustomerId = false) => {
      // Promo Login Mode (PromoLogin.js) has no Firebase uid to fall back
      // to, so it always takes the useCustomerId branch below — but
      // /merchantDetailsByCustomer/:customer_id is disabled backend-side
      // (see Settings.js's fetchMerchantDetails for the same situation) and
      // would just 404 silently, leaving the name blank. PromoLogin already
      // cached the merchant's name at login time; use that instead.
      if (useCustomerId && isPromoLoginMode()) {
        setContactName(localStorage.getItem("merchant_name") || "");
        setLoading(false);
        return;
      }

      try {
        const url = useCustomerId
          ? `${API_BASE}/merchantDetailsByCustomer/${id}`
          : `${API_BASE}/merchantDetails/${id}`;
        const response = await fetch(url);
        if (!response.ok) return;
        const data = await response.json();
        const info = data.data;
        setContactName(info?.name || info?.full_name || "");

        if (!useCustomerId && info) {
          const isTeamMember = Number(info.is_teammember) === 1;
          const isTeamMemberActive = Number(info.is_teammember_active) !== 0;

          if (isTeamMember && !isTeamMemberActive) {
            await auth.signOut();
            localStorage.removeItem("customer_id");
            localStorage.removeItem("phone");
            navigate("/login", { state: { disabledMessage: ACCESS_DISABLED_MESSAGE } });
            return;
          }

          // Not a member (owner) -> straight into the store, or an active
          // member -> into their assigned store: either way keep the
          // resolved store's customer_id in sync.
          if (info.customer_id) {
            localStorage.setItem("customer_id", info.customer_id);
          }
        }
      } catch (err) {
        console.error("Merchant home fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    const isAdminAsMerchant = !!localStorage.getItem("admin_token");
    if (isAdminAsMerchant) {
      fetchDetails(customerId, true);
      return;
    }

    const unsub = onIdTokenChanged(auth, (user) => {
      if (user) {
        fetchDetails(user.uid);
      } else if (customerId) {
        fetchDetails(customerId, true);
      } else {
        navigate("/login");
      }
    });

    return () => unsub();
  }, [navigate]);

  const firstName = contactName?.split(" ")[0] || "there";
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  // Stripe amounts are in the smallest currency unit (e.g. cents) — divide
  // by 100 and format with the currency Stripe reported, not a hardcoded $.
  const formatMoney = (amountInSmallestUnit, currency) =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: currency.toUpperCase() }).format(
      amountInSmallestUnit / 100
    );

  return (
    <div className="min-h-[100dvh] bg-white w-full min-w-0 overflow-x-hidden flex flex-col pb-24 sm:pb-28">
      {paymentSummary && (
        <div className="mx-4 sm:mx-5 mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-green-800">Payment confirmed</p>
              <p className="text-xs text-green-700 mt-1">
                Amount {formatMoney(paymentSummary.subtotal, paymentSummary.currency)}
                {paymentSummary.discount > 0 && (
                  <>
                    {" − "}
                    Discount {formatMoney(paymentSummary.discount, paymentSummary.currency)}
                  </>
                )}
                {" + "}
                Tax {formatMoney(paymentSummary.tax, paymentSummary.currency)}
                {" = "}
                <span className="font-semibold">
                  {formatMoney(paymentSummary.total, paymentSummary.currency)}
                </span>
              </p>
            </div>
            <button
              type="button"
              onClick={() => setPaymentSummary(null)}
              aria-label="Dismiss"
              className="text-green-700 text-sm leading-none"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-5 pt-6 flex flex-col gap-6">
        <div>
          <p className="text-sm text-gray-500 mb-1">{greeting}</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 leading-tight">
            {loading ? "Welcome!" : `Welcome, ${firstName}!`}
          </h1>
        </div>

        {!loading && <WorkflowSection />}
      </div>

      <BottomTabBar />
    </div>
  );
}
